import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Plus, Save, Trash2, Loader2, Eye, EyeOff, Star, Image as ImageIcon, X, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import AdminShell from "@/components/admin/AdminShell";
import { COLLECTION_SCHEMAS } from "@/lib/collectionSchemas";
import {
  listCollection,
  createCollectionItem,
  updateCollectionItem,
  deleteCollectionItem,
  reorderCollection,
  uploadMedia,
} from "@/lib/adminCmsApi";
import { errorToString } from "@/lib/errorUtils";

function defaultRow(schema) {
  const obj = {};
  schema.fields.forEach((f) => {
    if (f.default !== undefined) obj[f.key] = f.default;
    else if (f.type === "checkbox") obj[f.key] = false;
    else if (f.type === "list") obj[f.key] = [];
    else if (f.type === "number" || f.type === "rating") obj[f.key] = 0;
    else obj[f.key] = "";
  });
  return obj;
}

function FieldRenderer({ field, value, onChange }) {
  const baseClass =
    "w-full bg-white border border-[#0E2A57]/18 px-3 py-2 text-sm focus:outline-none focus:border-[#0E2A57]";

  if (field.type === "textarea") {
    return (
      <textarea
        data-testid={`field-${field.key}`}
        className={baseClass}
        rows={field.rows || 4}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          data-testid={`field-${field.key}`}
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 accent-[#0E2A57]"
        />
        <span className="text-sm text-ags-text-muted">{field.label}</span>
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <select
        data-testid={`field-${field.key}`}
        className={baseClass}
        value={value || field.default || field.options[0]}
        onChange={(e) => onChange(e.target.value)}
      >
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (field.type === "rating") {
    return (
      <div className="flex items-center gap-1" data-testid={`field-${field.key}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`p-1 ${n <= (value || 0) ? "text-[#FFB836]" : "text-[#E5EBF2]"}`}
          >
            <Star size={20} fill="currentColor" />
          </button>
        ))}
        <span className="ml-2 text-xs font-mono text-ags-text-muted">{value || 0}/5</span>
      </div>
    );
  }

  if (field.type === "list") {
    const items = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2" data-testid={`field-${field.key}`}>
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input
              className={baseClass}
              value={it}
              onChange={(e) => {
                const next = [...items];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button
              type="button"
              onClick={() => onChange(items.filter((_, x) => x !== i))}
              className="px-2 text-red-600 hover:text-red-800"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...items, ""])}
          className="inline-flex items-center gap-2 border border-[#0E2A57]/25 text-[#0E2A57] hover:bg-[#0E2A57] hover:text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em]"
        >
          <Plus size={12} /> {field.placeholder ? `Add (${field.placeholder})` : "Add item"}
        </button>
      </div>
    );
  }

  if (field.type === "image") {
    const onPick = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const m = await uploadMedia(file, { alt_text: field.label });
        onChange(m.url);
        toast.success("Image uploaded");
      } catch (err) {
        toast.error("Upload failed");
      }
    };
    return (
      <div className="flex items-start gap-4" data-testid={`field-${field.key}`}>
        {value ? (
          <img src={value} alt="" className="w-24 h-24 object-cover border border-[#0E2A57]/15" />
        ) : (
          <div className="w-24 h-24 bg-ags-cream border border-dashed border-[#0E2A57]/25 flex items-center justify-center">
            <ImageIcon size={20} className="text-[#0E2A57]/40" />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            className={baseClass}
            placeholder="https://… or upload below"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          <label className="inline-flex items-center gap-2 cursor-pointer bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em]">
            <Plus size={12} /> Upload
            <input type="file" accept="image/*" onChange={onPick} className="hidden" />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="ml-2 inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-mono text-[10px] uppercase tracking-[0.22em]"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
      </div>
    );
  }

  // text, url, email, number
  return (
    <input
      data-testid={`field-${field.key}`}
      className={baseClass}
      type={field.type === "number" ? "number" : field.type === "email" ? "email" : "text"}
      value={value || ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value || 0) : e.target.value)}
    />
  );
}

// Sortable row wraps a single collection list item with DnD handles.
function SortableRow({ row, schema, onEdit, onDelete, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const visKey = "is_active" in row ? "is_active" : "is_visible";
  const visible = row[visKey];
  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid={`collection-row-${row.id}`}
      className="px-5 md:px-6 py-5 flex flex-wrap items-center gap-4 hover:bg-ags-cream bg-white"
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`collection-drag-${row.id}`}
        className="cursor-grab active:cursor-grabbing text-[#0E2A57]/30 hover:text-[#0E2A57] p-1"
      >
        <GripVertical size={14} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-[15px] text-ags-ink tracking-[-0.02em] truncate">
          {schema.itemTitle(row) || "Untitled"}
        </div>
        {schema.itemSubtitle && (
          <div className="mt-0.5 text-xs text-ags-text-muted truncate">
            {schema.itemSubtitle(row)}
          </div>
        )}
      </div>
      <button
        onClick={() => onToggle(row)}
        data-testid={`collection-toggle-${row.id}`}
        className={`inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] border ${
          visible
            ? "border-[#2BB6E5] text-[#0E2A57] bg-[#BEE7F5]/30"
            : "border-[#0E2A57]/20 text-ags-text-muted"
        }`}
      >
        {visible ? <><Eye size={11} /> Visible</> : <><EyeOff size={11} /> Hidden</>}
      </button>
      <button
        onClick={() => onEdit(row)}
        data-testid={`collection-edit-${row.id}`}
        className="inline-flex items-center gap-1.5 bg-[#0E2A57] text-white hover:bg-[#050B14] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
      >
        Edit
      </button>
      <button
        onClick={() => onDelete(row)}
        data-testid={`collection-delete-${row.id}`}
        className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 hover:bg-red-700 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em]"
      >
        <Trash2 size={11} />
      </button>
    </li>
  );
}

export default function AdminCollection() {
  const { collection } = useParams();
  const schema = COLLECTION_SCHEMAS[collection];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = rows.findIndex((r) => r.id === active.id);
    const newIdx = rows.findIndex((r) => r.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const newRows = arrayMove(rows, oldIdx, newIdx);
    setRows(newRows); // optimistic
    try {
      await reorderCollection(schema.endpoint, newRows.map((r) => r.id));
      toast.success("Order saved");
    } catch (e) {
      toast.error("Failed to save order");
      load();
    }
  };

  const load = async () => {
    if (!schema) return;
    setLoading(true);
    try {
      const data = await listCollection(schema.endpoint);
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [collection]);

  if (!schema) return <Navigate to="/admin" replace />;

  const onNew = () => setEditing(defaultRow(schema));

  const onSave = async () => {
    // basic validation
    for (const f of schema.fields) {
      if (f.required && !String(editing[f.key] ?? "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    try {
      if (editing.id) {
        await updateCollectionItem(schema.endpoint, editing.id, editing);
        toast.success("Updated");
      } else {
        await createCollectionItem(schema.endpoint, editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorToString(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm("Delete this item permanently?")) return;
    try {
      await deleteCollectionItem(schema.endpoint, row.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleVisible = async (row) => {
    const key = "is_active" in row ? "is_active" : "is_visible";
    try {
      await updateCollectionItem(schema.endpoint, row.id, { ...row, [key]: !row[key] });
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <AdminShell title={schema.title}>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <p className="text-sm text-ags-text-muted max-w-2xl">{schema.description}</p>
        <button
          onClick={onNew}
          data-testid="collection-new"
          className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
        >
          <Plus size={14} /> New {schema.title.replace(/s$/i, "").toLowerCase()}
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-[#0E2A57]/15 p-12 flex items-center justify-center text-ags-text-muted">
          <Loader2 size={20} className="animate-spin mr-3" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#0E2A57]/15 p-16 text-center">
          <div className="font-heading font-bold text-lg text-ags-ink">Nothing here yet</div>
          <p className="text-sm text-ags-text-muted mt-2 mb-6">Create your first {schema.title.toLowerCase()} entry.</p>
          <button
            onClick={onNew}
            className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em]"
          >
            <Plus size={14} /> Add first item
          </button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <ul className="bg-white border border-[#0E2A57]/15 divide-y divide-ags-hairline">
              {rows.map((r) => (
                <SortableRow
                  key={r.id}
                  row={r}
                  schema={schema}
                  onEdit={setEditing}
                  onDelete={onDelete}
                  onToggle={toggleVisible}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      {/* Editor Drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="collection-editor">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <div className="w-full max-w-2xl bg-white shadow-2xl h-full overflow-y-auto">
            <div className="px-8 py-6 border-b border-ags-hairline flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/55">
                  {editing.id ? `Edit ${schema.title}` : `New ${schema.title}`}
                </div>
                <h2 className="mt-1 font-heading font-extrabold text-2xl tracking-[-0.02em] text-ags-ink">
                  {schema.itemTitle(editing) || `New ${schema.title}`}
                </h2>
              </div>
              <button
                onClick={() => setEditing(null)}
                data-testid="collection-editor-close"
                className="text-ags-text-muted hover:text-ags-ink font-mono text-[10px] uppercase tracking-[0.22em]"
              >
                Cancel
              </button>
            </div>

            <div className="px-8 py-6 space-y-5">
              {schema.fields.map((f) => (
                <div key={f.key}>
                  {f.type !== "checkbox" && (
                    <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#0E2A57]/70 mb-2">
                      {f.label}{f.required && " *"}
                    </label>
                  )}
                  <FieldRenderer
                    field={f}
                    value={editing[f.key]}
                    onChange={(v) => setEditing({ ...editing, [f.key]: v })}
                  />
                  {f.hint && <div className="mt-1 text-xs text-ags-text-muted">{f.hint}</div>}
                </div>
              ))}
            </div>

            <div className="px-8 py-5 border-t border-ags-hairline flex items-center justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setEditing(null)}
                className="px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted hover:text-ags-ink"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving}
                data-testid="collection-save"
                className="inline-flex items-center gap-2 bg-[#0E2A57] text-white hover:bg-[#050B14] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editing.id ? "Save changes" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
